import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CareersPage.css';
import { setPageTitle, pageTitles, pageDescriptions } from '../utils/pageTitle';
import { useLanguage } from '../contexts/LanguageContext';
import { openPositions } from '../data/careersData';

const CareersPage = () => {
  const { t, localizedPath, language } = useLanguage();

  useEffect(() => {
    setPageTitle(pageTitles.careers, pageDescriptions.careers, 'careers', language);
  }, [language]);

  const benefits = [
    // ... benefits remain same ...
    {
      icon: '🏠',
      title: t('careers.benefits.remote.title'),
      description: t('careers.benefits.remote.description')
    },
    {
      icon: '📚',
      title: t('careers.benefits.learning.title'),
      description: t('careers.benefits.learning.description')
    },
    {
      icon: '🏥',
      title: t('careers.benefits.health.title'),
      description: t('careers.benefits.health.description')
    },
    {
      icon: '💰',
      title: t('careers.benefits.salary.title'),
      description: t('careers.benefits.salary.description')
    },
    {
      icon: '🌴',
      title: t('careers.benefits.timeOff.title'),
      description: t('careers.benefits.timeOff.description')
    },
    {
      icon: '🚀',
      title: t('careers.benefits.growth.title'),
      description: t('careers.benefits.growth.description')
    }
  ];

  const values = [
    {
      title: t('careers.values.innovation.title'),
      description: t('careers.values.innovation.description')
    },
    {
      title: t('careers.values.collaboration.title'),
      description: t('careers.values.collaboration.description')
    },
    {
      title: t('careers.values.excellence.title'),
      description: t('careers.values.excellence.description')
    },
    {
      title: t('careers.values.growth.title'),
      description: t('careers.values.growth.description')
    }
  ];

  return (
    <div className="careers-page">
      <section className="careers-hero section">
        <div className="container">
          <div className="careers-hero-content">
            <h1 className="page-title">{t('careers.pageTitle')}</h1>
            <p className="careers-hero-description">
              {t('careers.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      <section className="company-culture section">
        <div className="container">
          <div className="culture-content">
            <div className="culture-text">
              <h2 className="section-title">{t('careers.whyWorkTitle')}</h2>
              <p>
                {t('careers.whyWorkParagraph1')}
              </p>
              <p>
                {t('careers.whyWorkParagraph2')}
              </p>
            </div>
            <div className="culture-values">
              <h3>{t('careers.ourValues')}</h3>
              <div className="values-list">
                {values.map((value, index) => (
                  <div key={index} className="value-item">
                    <h4 className="value-title">{value.title}</h4>
                    <p className="value-desc">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="benefits-section section">
        <div className="container">
          <h2 className="section-title">{t('careers.benefitsTitle')}</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {openPositions.length > 0 && (
        <section className="open-positions section" id="jobs">
          <div className="container">
            <h2 className="section-title">{t('careers.openPositionsTitle')}</h2>
            <div className="positions-grid">
              {openPositions.map((position) => (
                <div key={position.id} className="position-card">
                  <div className="position-header">
                    <div className="position-title-row">
                      <h3 className="position-title">{t(`careers.jobs.${position.key}.title`)}</h3>
                      {position.status === 'hired' && (
                        <span className="position-hired-badge">{t('careers.positionFilledBadge')}</span>
                      )}
                    </div>
                    <div className="position-meta">
                      <span className="position-department">{t(`careers.departments.${position.department}`)}</span>
                      <span className="position-location">{t(`careers.locations.${position.location}`)}</span>
                      <span className="position-type">{t(`careers.types.${position.type}`)}</span>
                      <span className="position-experience">{t('careers.experience')} {position.experience}</span>
                    </div>
                  </div>

                  <div className="position-content">
                    <p className="position-description line-clamp">
                      {t(`careers.jobs.${position.key}.description`)}
                    </p>
                  </div>

                  <div className="position-footer">
                    <Link to={localizedPath(`/careers/${position.id}`)} className="btn-secondary" style={{ marginRight: '1rem' }}>
                      {t('careers.viewDetails')}
                    </Link>
                    {position.status === 'hired' ? (
                      <span className="apply-btn apply-btn-disabled">
                        {t('careers.positionFilled')}
                      </span>
                    ) : (
                      <a
                        href={`mailto:careers@radixtechs.com?subject=${encodeURIComponent(`Application for ${t(`careers.jobs.${position.key}.title`)} - ${t(`careers.locations.${position.location}`)}`)}&body=${encodeURIComponent(`Hi Radix Technologies Team,\n\nI would like to apply for the ${t(`careers.jobs.${position.key}.title`)} position (${t(`careers.types.${position.type}`)}) in ${t(`careers.locations.${position.location}`)}.\n\n[Please attach your CV/Resume and provide any additional details here]\n\nBest regards,`)}`}
                        className="apply-btn"
                      >
                        {t('careers.applyNow')}
                        <span className="apply-arrow">→</span>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      <section className="careers-cta section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">{t('careers.ctaTitle')}</h2>
            <p className="cta-description">
              {t('careers.ctaDescription')}
            </p>
            <a href="mailto:careers@radixtechs.com?subject=General Career Inquiry" className="btn-primary">
              {t('careers.getInTouch')}
              <span className="btn-icon">📧</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
